import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  SwipeableDrawer,
  ImageList,
  ImageListItem,
  IconButton,
  Stack,
  Divider,
  Button,
  Grid,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import { motion } from "framer-motion";
import SEO from "./SEO";

const TEMPLATES_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      "@id": "https://studio.wiyoutech.in/design-templates#page",
      "name": "Restaurant Menu & QR Design Gallery | WiYou! Tech Studios",
      "description": "Explore custom restaurant menu designs, café menus, and scan-ready QR code designs created by WiYou! Tech Studios in Jaipur, Rajasthan.",
      "url": "https://studio.wiyoutech.in/design-templates",
      "isPartOf": { "@type": "WebSite", "@id": "https://studio.wiyoutech.in/#website" }
    },
    {
      "@type": "ItemList",
      "name": "Design Gallery Collections",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Restaurant & Café Menu Designs",
          "description": "Custom print and digital menu designs engineered for restaurants, cafés, lounges, and bars."
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Scan-Ready QR Code Templates",
          "description": "Branded, high-conversion QR code menu templates and interactive digital menu entry points."
        }
      ]
    }
  ]
};

/**
 * Helper function to generate image paths dynamically.
 * @param {string} folder - The subfolder name.
 * @param {string} prefix - The file name prefix.
 * @param {number} count - Total number of images.
 */
const generateImagePaths = (folder, prefix, count) => {
  return Array.from(
    { length: count },
    (_, i) => `/assets/${folder}/${prefix}${i + 1}.jpg`
  );
};

const CATEGORIES = [
  {
    id: "menu",
    title: "Menu Designs",
    subtitle: "Restaurant & café menus",
    eyebrow: "01 / Food & Hospitality",
    images: generateImagePaths("templates", "", 6),
  },
  {
    id: "qr",
    title: "QR Templates",
    subtitle: "Scan-ready QR designs",
    eyebrow: "02 / Digital Touchpoints",
    images: generateImagePaths("qr", "qr", 45),
  },
];

const INK = "#2B211C";
const MUTED = "#75675E";
const ACCENT = "#A06E50";
const PAPER = "#F7F4EF";
const SURFACE = "#FCFAF6";
const LINE = "#DCD4CC";

const MotionBox = motion(Box);
const MotionCard = motion(Card);

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.65,
      delay,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const DesignGallery = () => {
  const [listDrawerOpen, setListDrawerOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeImages, setActiveImages] = useState([]);
  const [index, setIndex] = useState(0);

  const openCategory = (category) => {
    setActiveCategory(category);
    setActiveImages(category.images);
    setIndex(0);
    setListDrawerOpen(true);
  };

  const openViewer = (i) => {
    setIndex(i);
    setViewerOpen(true);
  };

  const closeViewer = useCallback(() => setViewerOpen(false), []);

  const next = useCallback(
    () =>
      setIndex((current) =>
        Math.min(current + 1, activeImages.length - 1)
      ),
    [activeImages.length]
  );

  const prev = useCallback(
    () => setIndex((current) => Math.max(current - 1, 0)),
    []
  );

  useEffect(() => {
    if (!viewerOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeViewer();
      if (event.key === "ArrowRight") next();
      if (event.key === "ArrowLeft") prev();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [viewerOpen, next, prev, closeViewer]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: 'transparent',
        color: INK,
        pt: { xs: 10, md: 15 },
        pb: 12,
        overflow: "hidden",
        "& .editorial-serif": {
          fontFamily: "Georgia, 'Times New Roman', serif",
        },
      }}
    >
      <SEO
        title="Restaurant Menu & QR Design Gallery | WiYou! Tech Studios Jaipur"
        description="Browse restaurant menu designs, café food menus, and scan-ready QR code designs by WiYou! Tech Studios in Jaipur, Rajasthan."
        keywords={[
          "restaurant menu design Jaipur",
          "QR menu templates Jaipur",
          "café menu design",
          "food menu design Jaipur",
          "digital menu templates",
          "WiYou Tech Studios templates"
        ]}
        canonicalUrl="https://studio.wiyoutech.in/design-templates"
        schema={TEMPLATES_SCHEMA}
      />
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2.5, sm: 4, md: 6 } }}>
        {/* ================= HERO ================= */}
        <MotionBox
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
          sx={{
            minHeight: { md: "62vh" },
            display: "flex",
            alignItems: "flex-end",
            pb: { xs: 8, md: 12 },
          }}
        >
          <Box sx={{ width: "100%" }}>
            <GridViewRoundedIcon
              sx={{
                color: ACCENT,
                fontSize: 25,
                mb: 2,
              }}
            />

            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: ".2em",
                textTransform: "uppercase",
                color: ACCENT,
                mb: 2,
              }}
            >
              WiYou! Tech / Design Library
            </Typography>

            <Typography
              component="h1"
              className="editorial-serif"
              sx={{
                fontSize: {
                  xs: "3.5rem",
                  sm: "5rem",
                  md: "7.5rem",
                },
                lineHeight: { xs: 0.94, md: 0.87 },
                letterSpacing: "-.07em",
                fontWeight: 700,
                maxWidth: 1050,
              }}
            >
              Designs made
              <br />
              to be noticed.
            </Typography>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 2, md: 6 }}
              sx={{
                mt: 4,
                pt: 3,
                borderTop: `1px solid ${LINE}`,
                maxWidth: 1050,
              }}
            >
              <Typography
                sx={{
                  color: MUTED,
                  fontSize: 15,
                  lineHeight: 1.8,
                  maxWidth: 530,
                }}
              >
                Explore the WiYou! Tech design library. Browse ready-made
                visual directions for menus, QR experiences, and digital
                touchpoints.
              </Typography>

              <Typography
                sx={{
                  color: MUTED,
                  fontSize: 13,
                  lineHeight: 1.7,
                  maxWidth: 260,
                }}
              >
                Select a collection to open its full visual archive.
              </Typography>
            </Stack>
          </Box>
        </MotionBox>

        <Divider sx={{ borderColor: LINE }} />

        {/* ================= COLLECTIONS ================= */}
        <Box sx={{ py: { xs: 8, md: 12 } }}>
          <MotionBox
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            sx={{ mb: 7 }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: ".2em",
                textTransform: "uppercase",
                color: ACCENT,
              }}
            >
              01 — Collections
            </Typography>

            <Typography
              className="editorial-serif"
              sx={{
                mt: 1.5,
                fontSize: { xs: "2.8rem", md: "5rem" },
                lineHeight: 0.95,
                letterSpacing: "-.055em",
              }}
            >
              Browse the library.
            </Typography>
          </MotionBox>

          <Stack spacing={0}>
            {CATEGORIES.map((cat, categoryIndex) => (
              <MotionCard
                key={cat.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.15 }}
                custom={categoryIndex * 0.08}
                elevation={0}
                sx={{
                  bgcolor: "transparent",
                  border: 0,
                  borderTop: `1px solid ${LINE}`,
                  borderRadius: 0,
                  boxShadow: "none",
                  overflow: "hidden",
                }}
              >
                <CardActionArea
                  onClick={() => openCategory(cat)}
                  sx={{
                    "&:hover .collection-arrow": {
                      transform: "translateX(5px)",
                    },
                    "&:hover .collection-image": {
                      transform: "scale(1.025)",
                    },
                  }}
                >
                  <Grid
                    container
                    spacing={{ xs: 3, md: 7 }}
                    alignItems="center"
                    sx={{ py: { xs: 4, md: 5 } }}
                  >
                    {/* Preview */}
                    <Grid item xs={12} md={5}>
                      <Box
                        sx={{
                          height: { xs: 240, sm: 310, md: 350 },
                          overflow: "hidden",
                          bgcolor: SURFACE,
                          border: `1px solid ${LINE}`,
                        }}
                      >
                        <Box
                          component="img"
                          className="collection-image"
                          src={cat.images[0]}
                          alt={`${cat.title} preview`}
                          sx={{
                            display: "block",
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            transition: "transform .7s cubic-bezier(.22,1,.36,1)",
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Content */}
                    <Grid item xs={12} md={7}>
                      <CardContent sx={{ p: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: ".18em",
                            textTransform: "uppercase",
                            color: ACCENT,
                            mb: 2,
                          }}
                        >
                          {cat.eyebrow}
                        </Typography>

                        <Typography
                          className="editorial-serif"
                          sx={{
                            fontSize: { xs: "2.3rem", md: "4rem" },
                            lineHeight: 0.98,
                            letterSpacing: "-.05em",
                          }}
                        >
                          {cat.title}
                        </Typography>

                        <Typography
                          sx={{
                            color: MUTED,
                            mt: 1.5,
                            maxWidth: 480,
                            lineHeight: 1.8,
                            fontSize: 14,
                          }}
                        >
                          {cat.subtitle}. Explore the complete collection and
                          find a direction that fits your project.
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mt: 3,
                            color: INK,
                            fontSize: 13,
                            fontWeight: 800,
                          }}
                        >
                          Open collection
                          <ArrowForwardRoundedIcon
                            className="collection-arrow"
                            sx={{
                              fontSize: 18,
                              transition: "transform .3s ease",
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Grid>
                  </Grid>
                </CardActionArea>
              </MotionCard>
            ))}
          </Stack>
        </Box>

        {/* ================= FOOTNOTE ================= */}
        <Box
          sx={{
            borderTop: `1px solid ${LINE}`,
            pt: 3,
            display: "flex",
            justifyContent: "space-between",
            gap: 3,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              color: MUTED,
              letterSpacing: ".08em",
              textTransform: "uppercase",
            }}
          >
            WiYou! Tech Studios
          </Typography>

          <Typography sx={{ fontSize: 12, color: MUTED }}>
            Select a collection to explore.
          </Typography>
        </Box>
      </Box>

      {/* ================= CATEGORY DRAWER ================= */}
      <SwipeableDrawer
        anchor="right"
        open={listDrawerOpen}
        onClose={() => setListDrawerOpen(false)}
        onOpen={() => {}}
        PaperProps={{
          sx: {
            width: "100%",
            height: "100%",
            bgcolor: PAPER,
            color: INK,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1500,
            mx: "auto",
            px: { xs: 2, sm: 3, md: 5 },
            py: { xs: 2, md: 3 },
            height: "100%",
            overflowY: "auto",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              pb: 2,
              borderBottom: `1px solid ${LINE}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton
                onClick={() => setListDrawerOpen(false)}
                sx={{
                  width: 42,
                  height: 42,
                  border: `1px solid ${LINE}`,
                  color: INK,
                  borderRadius: 0,
                }}
              >
                <CloseRoundedIcon />
              </IconButton>

              <Box>
                <Typography
                  sx={{
                    fontSize: 9,
                    letterSpacing: ".18em",
                    textTransform: "uppercase",
                    color: ACCENT,
                    fontWeight: 800,
                  }}
                >
                  Collection
                </Typography>

                <Typography
                  className="editorial-serif"
                  sx={{
                    fontSize: { xs: 22, md: 28 },
                    lineHeight: 1,
                    mt: 0.5,
                  }}
                >
                  {activeCategory?.title}
                </Typography>
              </Box>
            </Stack>

            <Typography
              sx={{
                display: { xs: "none", sm: "block" },
                color: MUTED,
                fontSize: 12,
              }}
            >
              {activeImages.length} designs
            </Typography>
          </Stack>

          <Box sx={{ pt: 3, pb: 5 }}>
            <ImageList
              variant="masonry"
              cols={1}
              gap={16}
              sx={{
                columnCount: {
                  xs: "1 !important",
                  sm: "2 !important",
                  md: "3 !important",
                  lg: "4 !important",
                  xl: "5 !important",
                },
              }}
            >
              {activeImages.map((img, i) => (
                <ImageListItem
                  key={img}
                  onClick={() => openViewer(i)}
                  sx={{
                    cursor: "zoom-in",
                    overflow: "hidden",
                    bgcolor: SURFACE,
                    border: `1px solid ${LINE}`,
                    "& img": {
                      display: "block",
                      width: "100%",
                      transition:
                        "transform .55s cubic-bezier(.22,1,.36,1)",
                    },
                    "&:hover img": {
                      transform: "scale(1.035)",
                    },
                  }}
                >
                  <img
                    src={img}
                    alt={`${activeCategory?.title || "Design"} ${i + 1}`}
                    loading="lazy"
                  />

                  <Box
                    sx={{
                      position: "absolute",
                      left: 10,
                      bottom: 10,
                      px: 1,
                      py: 0.5,
                      bgcolor: "rgba(247,244,239,.92)",
                      color: INK,
                      fontSize: 9,
                      fontWeight: 800,
                      letterSpacing: ".08em",
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </Box>
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        </Box>
      </SwipeableDrawer>

      {/* ================= FULL VIEWER ================= */}
      <SwipeableDrawer
        anchor="right"
        open={viewerOpen}
        onClose={closeViewer}
        onOpen={() => {}}
        PaperProps={{
          sx: {
            width: "100%",
            height: "100%",
            bgcolor: INK,
            color: PAPER,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            px: { xs: 1.5, md: 3 },
            py: 2,
          }}
        >
          {/* Viewer Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              pb: 2,
              borderBottom: "1px solid rgba(247,244,239,.18)",
            }}
          >
            <IconButton
              onClick={closeViewer}
              sx={{
                width: 42,
                height: 42,
                color: PAPER,
                border: "1px solid rgba(247,244,239,.25)",
                borderRadius: 0,
              }}
            >
              <CloseRoundedIcon />
            </IconButton>

            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: 9,
                  letterSpacing: ".18em",
                  textTransform: "uppercase",
                  color: "#C9A78F",
                  fontWeight: 800,
                }}
              >
                {activeCategory?.title}
              </Typography>

              <Typography
                className="editorial-serif"
                sx={{
                  fontSize: { xs: 18, md: 22 },
                  lineHeight: 1,
                  mt: 0.5,
                }}
              >
                {index + 1} / {activeImages.length}
              </Typography>
            </Box>

            <Box sx={{ width: 42 }} />
          </Stack>

          {/* Image */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 0.5, md: 2 },
              py: 2,
            }}
          >
            <IconButton
              onClick={prev}
              disabled={index === 0}
              aria-label="Previous design"
              sx={{
                flexShrink: 0,
                color: PAPER,
                width: { xs: 42, md: 54 },
                height: { xs: 42, md: 54 },
                border: "1px solid rgba(247,244,239,.25)",
                borderRadius: 0,
                "&.Mui-disabled": {
                  color: "rgba(247,244,239,.22)",
                },
              }}
            >
              <ChevronLeftRoundedIcon fontSize="large" />
            </IconButton>

            <Box
              component="img"
              src={activeImages[index]}
              alt={`Design ${index + 1}`}
              sx={{
                maxHeight: "calc(100vh - 150px)",
                maxWidth: "calc(100vw - 150px)",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                display: "block",
                boxShadow: "0 24px 70px rgba(0,0,0,.3)",
                bgcolor: "#fff",
              }}
            />

            <IconButton
              onClick={next}
              disabled={index === activeImages.length - 1}
              aria-label="Next design"
              sx={{
                flexShrink: 0,
                color: PAPER,
                width: { xs: 42, md: 54 },
                height: { xs: 42, md: 54 },
                border: "1px solid rgba(247,244,239,.25)",
                borderRadius: 0,
                "&.Mui-disabled": {
                  color: "rgba(247,244,239,.22)",
                },
              }}
            >
              <ChevronRightRoundedIcon fontSize="large" />
            </IconButton>
          </Box>

          {/* Viewer Footer */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            gap={1}
            sx={{
              pt: 2,
              borderTop: "1px solid rgba(247,244,239,.18)",
            }}
          >
            <Typography
              sx={{
                color: "rgba(247,244,239,.55)",
                fontSize: 11,
              }}
            >
              Use ← → to navigate · Esc to close
            </Typography>

            <Button
              onClick={closeViewer}
              sx={{
                color: PAPER,
                textTransform: "none",
                fontWeight: 700,
                px: 0,
                minWidth: 0,
                "&:hover": {
                  bgcolor: "transparent",
                  color: "#C9A78F",
                },
              }}
            >
              Back to collection
            </Button>
          </Stack>
        </Box>
      </SwipeableDrawer>
    </Box>
  );
};

export default DesignGallery;
